/**
 * Team › Their choices: one self-employed stylist's choices as chips, in the
 * table's column order. Pure (tested in choiceChips.test.js).
 *
 * `choices` is one row's `choices` from GET /team/se-choices. Tones:
 *   blue  the stylist has said yes / shared / joined in
 *   grey  kept to themselves, or nothing to show
 *   warn  worth a look (picked people only, payments not ready)
 *   ok    payments connected
 */

import { DISCOUNTS_VISIBLE } from '../../../config/featureFlags';

export const CHOICE_COLUMNS = [
  { key: 'clients', label: 'Clients' },
  { key: 'diary', label: 'Diary' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'loyalty', label: 'Loyalty' },
  // Discount codes (Oct 2026): their answer on the salon's codes
  ...(DISCOUNTS_VISIBLE ? [{ key: 'codes', label: 'Owner’s codes' }] : []),
  { key: 'bookingPage', label: 'Booking page' },
  { key: 'prices', label: 'Prices' },
  { key: 'payments', label: 'Payments' },
];

const chip = (label, tone, hint) => ({ label, tone, hint });

const PAYMENTS = {
  connected: chip('Connected', 'ok', 'Card payments for their bookings land in their own account.'),
  not_set_up: chip('Not set up', 'warn', 'They haven’t connected an account for card payments yet.'),
  action_needed: chip('Needs details', 'warn', 'Stripe needs more details from them before they can be paid.'),
  unknown: chip('Can’t tell', 'grey', 'Payments couldn’t be checked just now.'),
};

const PRICES = {
  salon: chip('Salon prices', 'blue', 'One salon menu, same prices for everyone.'),
  salonOwnPrices: chip('Own prices', 'grey', 'The salon menu, with their own prices.'),
  own: chip('Own menu', 'grey', 'They run their own menu and prices.'),
};

export function choiceChip(key, choices) {
  const c = choices || {};
  switch (key) {
    case 'clients':
      return c.clients && Number(c.clients.share) === 1
        ? chip('Shared', 'blue', 'The salon can see and book their clients.')
        : chip('Private', 'grey', 'Only they can see their clients.');
    case 'diary': {
      const v = c.diary && c.diary.visibility;
      if (v === 'nobody') return chip('Nobody', 'grey', 'Hidden from the team. The owner can still see it.');
      if (v === 'selected') {
        const n = Number(c.diary.pickedCount) || 0;
        return chip('People they pick', 'warn', n ? `${n} ${n === 1 ? 'person' : 'people'} on the team can see it.` : 'Nobody picked yet.');
      }
      return chip('Everyone', 'blue', 'Anyone on the team can see it and book them in.');
    }
    case 'marketing': {
      const m = c.marketing || {};
      if (m.bySalon) return chip('Salon’s', 'blue', 'The salon markets to their clients.');
      if (m.offered) return chip('Own clients', 'grey', 'Offered, but they market to their own clients.');
      return chip('Own clients', 'grey', 'They market to their own clients.');
    }
    case 'loyalty': {
      const l = c.loyalty;
      if (!l) return chip('No scheme', 'grey', 'The salon isn’t running a loyalty scheme.');
      if (l.joined) return chip('Joined', 'blue', 'Their clients earn on the salon’s scheme.');
      if (!l.offered) return chip('Not offered', 'grey', 'The scheme hasn’t been offered to them.');
      return chip('Not joined', 'grey', 'Offered, but they haven’t joined.');
    }
    case 'codes': {
      // { salonPays, shared } once they've answered; null until then
      const k = c.codes;
      if (!k) return chip('Not chosen', 'warn', 'Your codes don’t work on their services until they choose.');
      if (k.shared) return chip('Shared', 'blue', 'Your codes work on their services, paid by you or shared in line with their split.');
      if (k.salonPays) return chip('If you pay', 'blue', 'Your codes work on their services when you pay for them.');
      return chip('No', 'grey', 'Your codes don’t work on their services.');
    }
    case 'bookingPage':
      return c.bookingPage && c.bookingPage.shown === false
        ? chip('Hidden', 'grey', 'Not shown on the salon’s booking page.')
        : chip('Shown', 'blue', 'Clients can pick them on the salon’s booking page.');
    case 'prices':
      return PRICES[c.prices] || chip('Not set', 'grey', 'No menu model chosen in setup.');
    case 'payments':
      return PAYMENTS[c.payments] || PAYMENTS.unknown;
    default:
      return chip('', 'grey', '');
  }
}

/** Every chip for a row, in column order: [{ key, column, label, tone, hint }]. */
export function choiceChips(choices) {
  return CHOICE_COLUMNS.map((col) => ({ key: col.key, column: col.label, ...choiceChip(col.key, choices) }));
}

/** What tapping a chip says. The choice is theirs alone. */
export function onlyTheyCanChange(name) {
  const first = String(name || '').trim().split(/\s+/)[0];
  return `Only ${first || 'they'} can change this.`;
}
